import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/context/AuthContext";
import { postAiJob } from "@/utils/aiJobs";
import {
  BarChart3, TrendingUp, Gauge, Sparkles, Target, ShieldCheck,
  ArrowRight, Loader2, Check, X, AlertTriangle, Brain, FileText,
  Clock, MapPin, Euro, Percent, Copy, CheckCircle2, Lightbulb,
  Archive, Swords, Plus, Search, Tag, Trash2, Edit, Wand2, BookOpen, Trophy,
  Building2, BookMarked, Users, Signal, ClipboardCheck, PhoneCall
} from "lucide-react";
import { toast } from "sonner";
import { ESTADO_LABELS, ESTADO_CLS, fmtEur, Field, Kpi } from "./shared";

const STAGE_META = {
  descubrimiento:     { label: "Descubrimiento",       pct:  10, color: "bg-gray-300" },
  primer_contacto:    { label: "Primer contacto",      pct:  30, color: "bg-blue-400" },
  reunion_tecnica:    { label: "Reunión técnica",      pct:  55, color: "bg-indigo-500" },
  propuesta_informal: { label: "Propuesta informal",   pct:  80, color: "bg-amber-500" },
  seguimiento:        { label: "Seguimiento activo",   pct: 100, color: "bg-[#007934]" },
};
const TIER_CLS = { A: "bg-rose-500 text-white", B: "bg-amber-500 text-white", C: "bg-gray-400 text-white" };

export default function AccountsCRM() {
  const [data, setData] = useState(null);
  const [region, setRegion] = useState("");
  const [tier, setTier] = useState("");
  const [minMm, setMinMm] = useState(0); // Iter283 · filtro de importe persistente
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [detectingSignals, setDetectingSignals] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (region) params.append("region", region);
    if (tier)   params.append("tier", tier);
    if (minMm)  params.append("min_mm", minMm);
    try {
      const r = await axios.get(`${API}/tender360/accounts?${params.toString()}`);
      setData(r.data);
      if (selected) {
        const upd = (r.data.items || []).find(it => it.id === selected.id);
        if (upd) setSelected(upd);
      }
    } catch { toast.error("Error cargando accounts"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [region, tier, minMm]);

  const detectSignals = async () => {
    if (!selected) return;
    setDetectingSignals(true);
    try {
      const res = await postAiJob(`${API}/tender360/accounts/${selected.id}/signals/ai-detect`);
      toast.success("Señales detectadas");
      setSelected({ ...selected, signals: res.signals });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Error Pando"); }
    finally { setDetectingSignals(false); }
  };

  const updateStage = async (stage) => {
    if (!selected) return;
    try {
      const r = await axios.put(`${API}/tender360/accounts/${selected.id}`, { playbook_stage: stage });
      setSelected(r.data); toast.success("Etapa actualizada"); load();
    } catch { toast.error("Error"); }
  };

  const del = async () => {
    if (!selected || !window.confirm(`¿Eliminar ${selected.council}?`)) return;
    try { await axios.delete(`${API}/tender360/accounts/${selected.id}`); toast.success("Eliminado"); setSelected(null); load(); }
    catch { toast.error("Error"); }
  };

  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14}/> Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="accounts-crm">
      <div className="grid grid-cols-4 gap-3">
        <Kpi label="Accounts 360º"       val={data.kpis.total}/>
        <Kpi label="Activos"             val={data.kpis.active}/>
        <Kpi label="Tier A (prioritario)" val={data.kpis.tier_a}/>
        <Kpi label="Vencen < 12 meses"    val={data.kpis.pronto_vencen}/>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-center gap-2">
        <select value={region} onChange={e=>setRegion(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs bg-white">
          <option value="">Todas las regiones</option>
          {data.regions.map(r => <option key={r}>{r}</option>)}
        </select>
        <select value={tier} onChange={e=>setTier(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs bg-white">
          <option value="">Todos los tiers</option>
          <option>A</option><option>B</option><option>C</option>
        </select>
        {/* Iter283 · Importe ≥ X M€ — filtro persistente pedido por el usuario */}
        <div
          data-testid="crm-filter-importe-wrap"
          title="Filtra accounts por importe del contrato actual"
          className={`inline-flex items-center gap-1.5 border rounded-lg px-2 py-1 text-[11px] ${
            minMm > 0 ? "border-sky-300 bg-sky-50" : "border-gray-200 bg-white"
          }`}>
          <span className={minMm > 0 ? "font-semibold text-sky-700" : "text-gray-500"}>Importe ≥</span>
          <input
            type="range" min={0} max={30} step={1}
            value={minMm}
            onChange={(e) => setMinMm(Number(e.target.value))}
            data-testid="crm-filter-importe"
            className="accent-sky-500 w-20"
          />
          <span className={`font-mono w-12 text-right ${minMm > 0 ? "text-sky-700 font-bold" : "text-gray-500"}`}>{minMm}M€</span>
        </div>
        <div className="flex-1"/>
        <button onClick={()=>setShowForm(true)}
                className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-[#006528]"
                data-testid="account-add-btn">
          <Plus size={13}/> Nuevo account
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="max-h-[65vh] overflow-y-auto divide-y divide-gray-100">
            {(data.items || []).map(a => {
              const sm = STAGE_META[a.playbook_stage] || STAGE_META.descubrimiento;
              return (
                <button key={a.id} onClick={()=>setSelected(a)}
                        className={`w-full text-left p-3 hover:bg-gray-50 ${selected?.id===a.id ? "bg-gray-50" : ""}`}
                        data-testid={`account-item-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold text-gray-900 truncate">{a.council}</div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${TIER_CLS[a.tier] || "bg-gray-200"}`}>{a.tier}</span>
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{a.municipio} · {a.poblacion?.toLocaleString()} hab. · incumbente <strong>{a.incumbente || "?"}</strong></div>
                  <div className="mt-1">
                    <div className="flex items-center justify-between text-[9px] mb-0.5">
                      <span className="text-gray-500">{sm.label}</span>
                      <span className="text-gray-400">{a.contrato_actual_fin || "—"}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1"><div className={`${sm.color} h-full rounded-full`} style={{width:`${sm.pct}%`}}/></div>
                  </div>
                </button>
              );
            })}
            {!data.items?.length && <div className="p-6 text-center text-gray-400 text-xs">Sin accounts con ese filtro</div>}
          </div>
        </div>

        <div className="lg:col-span-3">
          {!selected
            ? <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-xs">Selecciona un account para ver el 360º</div>
            : <AccountDetail acc={selected} onStageChange={updateStage} onLogActivity={()=>setShowActivity(true)}
                             onDetectSignals={detectSignals} detecting={detectingSignals} onDelete={del}/>}
        </div>
      </div>

      {showForm && <AccountForm onClose={()=>{setShowForm(false);load();}}/>}
      {showActivity && selected && (
        <ActivityForm accId={selected.id} onClose={()=>{setShowActivity(false);load();}}/>
      )}
    </div>
  );
}

function AccountDetail({ acc, onStageChange, onLogActivity, onDetectSignals, detecting, onDelete }) {
  const stages = Object.keys(STAGE_META);
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4" data-testid="account-detail">
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
        <div>
          <div className="text-sm font-bold text-gray-900">{acc.council}</div>
          <div className="text-xs text-gray-500 mt-0.5">{acc.municipio} · {acc.poblacion?.toLocaleString()} hab. · {acc.region}</div>
        </div>
        <button onClick={onDelete} className="text-xs text-rose-600 hover:bg-rose-50 p-1.5 rounded" data-testid="account-delete-btn"><Trash2 size={12}/></button>
      </div>

      {/* Playbook stage selector */}
      <div>
        <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1">Etapa playbook</div>
        <div className="grid grid-cols-5 gap-1">
          {stages.map(s => {
            const active = acc.playbook_stage === s;
            return (
              <button key={s} onClick={()=>onStageChange(s)}
                      className={`text-[10px] p-1.5 rounded border text-center ${active ? "bg-[#007934] text-white border-[#007934]" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                      data-testid={`stage-btn-${s}`}>
                {STAGE_META[s].label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div><span className="text-gray-500">Contrato actual:</span> <span className="font-semibold">{acc.incumbente || "—"}</span></div>
        <div><span className="text-gray-500">Fin contrato:</span> <span className="font-semibold">{acc.contrato_actual_fin || "—"}</span></div>
        <div><span className="text-gray-500">Importe:</span> <span className="font-mono font-semibold">{fmtEur(acc.importe_contrato_actual_eur)}</span></div>
        <div><span className="text-gray-500">Servicio:</span> <span className="font-semibold">{acc.tipo_servicio}</span></div>
      </div>

      {acc.notas_estrategicas && <div className="text-xs text-gray-700 bg-amber-50 border-l-4 border-amber-400 p-2 rounded"><strong>Notas:</strong> {acc.notas_estrategicas}</div>}

      {/* Contactos */}
      {(acc.contactos || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1 flex items-center gap-1"><Users size={11}/> Contactos</div>
          <div className="space-y-1">
            {acc.contactos.map((c,i) => (
              <div key={i} className="text-xs border border-gray-100 rounded p-2">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-gray-900">{c.nombre}</div>
                  <div className="text-[10px] text-gray-500">{c.cargo}</div>
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">{c.email} {c.tel && `· ${c.tel}`}</div>
                {c.notas && <div className="text-[10px] text-gray-600 mt-1 italic">{c.notas}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline actividad */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide flex items-center gap-1"><ClipboardCheck size={11}/> Actividad</div>
          <button onClick={onLogActivity} className="text-[10px] bg-[#007934] text-white px-2 py-0.5 rounded font-semibold hover:bg-[#006528] flex items-center gap-1" data-testid="activity-add-btn">
            <Plus size={10}/> Registrar actividad
          </button>
        </div>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {(acc.activities || []).slice().reverse().map(a => (
            <div key={a.id} className="text-xs border-l-2 border-gray-300 pl-2 py-1">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-gray-900">{a.type} · {a.date}</div>
                <div className="text-[10px] text-gray-400">{a.person}</div>
              </div>
              <div className="text-gray-600 mt-0.5">{a.summary}</div>
              {a.next_step && <div className="text-[10px] text-[#007934] font-semibold mt-1">➜ {a.next_step} {a.next_step_date && `(${a.next_step_date})`}</div>}
            </div>
          ))}
          {!acc.activities?.length && <div className="text-[11px] text-gray-400 italic">Sin actividad registrada</div>}
        </div>
      </div>

      {/* Señales Pando */}
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide flex items-center gap-1"><Signal size={11}/> Señales tempranas Pando</div>
          <button onClick={onDetectSignals} disabled={detecting}
                  className="text-[10px] bg-[#007934] text-white px-2 py-1 rounded font-semibold hover:bg-[#006528] disabled:opacity-40 flex items-center gap-1"
                  data-testid="signals-detect-btn">
            {detecting ? <Loader2 className="animate-spin" size={10}/> : <Wand2 size={10}/>}
            {acc.signals ? "Regenerar" : "Detectar con Pando"}
          </button>
        </div>
        {acc.signals ? (
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#007934]">{acc.signals.probabilidad_licitacion_proximos_12m}%</div>
                <div className="text-[9px] uppercase text-gray-500">prob. 12m</div>
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase text-gray-500 font-semibold">Horizonte</div>
                <div className="font-semibold">{acc.signals.horizonte_estimado}</div>
                <div className="text-[10px] uppercase text-gray-500 font-semibold mt-1">Incumbente Pando</div>
                <div className="font-semibold">{acc.signals.competidor_incumbente || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-gray-500 font-semibold">Riesgo cambio</div>
                <span className={`inline-block px-2 py-1 rounded text-[10px] font-bold ${
                  acc.signals.riesgo_cambio_modelo === "alto" ? "bg-rose-100 text-rose-700" :
                  acc.signals.riesgo_cambio_modelo === "medio" ? "bg-amber-100 text-amber-700" :
                  "bg-green-100 text-green-700"}`}>{acc.signals.riesgo_cambio_modelo}</span>
              </div>
            </div>
            <p className="text-gray-700 text-[11px] italic">{acc.signals.resumen}</p>
            {(acc.signals.senales_detectadas || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase text-gray-500 font-semibold mb-1">Señales</div>
                <ul className="space-y-1">
                  {acc.signals.senales_detectadas.map((s, i) => (
                    <li key={i} className="text-[11px] flex items-start gap-2">
                      <span className={`mt-0.5 text-[9px] px-1.5 py-0.5 rounded font-bold ${
                        s.fuerza === "alta" ? "bg-rose-100 text-rose-700" :
                        s.fuerza === "media" ? "bg-amber-100 text-amber-700" :
                        "bg-gray-200 text-gray-600"}`}>{s.fuerza}</span>
                      <span className="flex-1">{s.senal} <span className="text-gray-400">({s.fecha_observada})</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(acc.signals.acciones_recomendadas || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase text-gray-500 font-semibold mb-1">Acciones recomendadas</div>
                <ul className="list-disc pl-4 text-[11px] text-gray-700">
                  {acc.signals.acciones_recomendadas.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-gray-500 italic">Clica "Detectar con Pando" para que el motor analice la actividad del account e identifique señales tempranas.</div>
        )}
      </div>
    </div>
  );
}

function AccountForm({ onClose }) {
  const [form, setForm] = useState({
    council: "", municipio: "", poblacion: "", region: "Madrid",
    contrato_actual_fin: "", incumbente: "", importe_contrato_actual_eur: "",
    tipo_servicio: "RSU", tier: "B", notas_estrategicas: "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.council) { toast.error("Indica el ayuntamiento"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/tender360/accounts`, {
        ...form,
        poblacion: form.poblacion ? parseInt(form.poblacion) : null,
        importe_contrato_actual_eur: form.importe_contrato_actual_eur ? parseFloat(form.importe_contrato_actual_eur) : null,
      });
      toast.success("Account creado"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full p-5" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">Nuevo account 360º</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={16}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <Field label="Ayuntamiento *"><input value={form.council} onChange={e=>setForm({...form,council:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Municipio"><input value={form.municipio} onChange={e=>setForm({...form,municipio:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Población"><input type="number" value={form.poblacion} onChange={e=>setForm({...form,poblacion:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Región"><input value={form.region} onChange={e=>setForm({...form,region:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Tier">
            <select value={form.tier} onChange={e=>setForm({...form,tier:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1 bg-white">
              <option>A</option><option>B</option><option>C</option>
            </select>
          </Field>
          <Field label="Tipo servicio"><input value={form.tipo_servicio} onChange={e=>setForm({...form,tipo_servicio:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Incumbente"><input value={form.incumbente} onChange={e=>setForm({...form,incumbente:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Fin contrato (YYYY-MM-DD)"><input value={form.contrato_actual_fin} onChange={e=>setForm({...form,contrato_actual_fin:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Importe actual (€)"><input type="number" value={form.importe_contrato_actual_eur} onChange={e=>setForm({...form,importe_contrato_actual_eur:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Notas estratégicas" span={2}><textarea value={form.notas_estrategicas} onChange={e=>setForm({...form,notas_estrategicas:e.target.value})} rows={3} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs border border-gray-200 px-3 py-1.5 rounded">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs bg-[#007934] text-white px-3 py-1.5 rounded flex items-center gap-1">
            {saving && <Loader2 className="animate-spin" size={12}/>} Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

function ActivityForm({ accId, onClose }) {
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({ type: "visita", date: today, person: "", summary: "", next_step: "", next_step_date: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.summary) { toast.error("Añade un resumen"); return; }
    setSaving(true);
    try { await axios.post(`${API}/tender360/accounts/${accId}/activity`, form); toast.success("Actividad registrada"); onClose(); }
    catch { toast.error("Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-xl w-full p-5" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">Registrar actividad</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={16}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <Field label="Tipo">
            <select value={form.type} onChange={e=>setForm({...form,type:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1 bg-white">
              <option>visita</option><option>llamada</option><option>email</option><option>reunion</option><option>evento</option><option>otro</option>
            </select>
          </Field>
          <Field label="Fecha"><input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Persona" span={2}><input value={form.person} onChange={e=>setForm({...form,person:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Resumen *" span={2}><textarea value={form.summary} onChange={e=>setForm({...form,summary:e.target.value})} rows={3} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Siguiente paso"><input value={form.next_step} onChange={e=>setForm({...form,next_step:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Fecha siguiente paso"><input type="date" value={form.next_step_date} onChange={e=>setForm({...form,next_step_date:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs border border-gray-200 px-3 py-1.5 rounded">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs bg-[#007934] text-white px-3 py-1.5 rounded flex items-center gap-1">
            {saving && <Loader2 className="animate-spin" size={12}/>} Registrar
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// M7: Post-Mortem Roadmap
// ═══════════════════════════════════════════════════════════════════════════
